// Solution: Get customer-scoped credentials using Cognito Identity Pool
import { Auth } from 'aws-amplify';
import awsconfig from '../aws-exports';
const AWS = require('aws-sdk');

/**
 * Store current customer credentials in memory
 */
let currentCustomerCredentials = null;
let currentCustomerConfig = null;

/**
 * Get AWS credentials scoped to a specific customer group.
 * Uses Cognito Identity Pool with CustomRoleArn parameter.
 *
 * For admin users the admin IAM role is used so they can access any customer's
 * S3 bucket. The bucket itself is always the one belonging to `customerID`.
 *
 * @param {string} customerID - Customer ID (e.g., "acme", "jelco")
 * @returns {Promise<Object|null>} AWS credentials object, or null on failure
 */
export async function getEnhancedAuthCredentials(customerID) {
  try {
    const user = await Auth.currentAuthenticatedUser({ bypassCache: false });
    const groups = user.signInUserSession.idToken.payload['cognito:groups'] || [];
    const isAdmin = groups.includes('admin');

    // Admin users can access any customer's bucket via the admin role.
    // Non-admin users must be a member of the target customer's Cognito group.
    if (!isAdmin && !groups.includes(customerID)) {
      throw new Error(`User not in group: ${customerID}`);
    }

    const token = user.signInUserSession.idToken.jwtToken;

    // Use CognitoIdentity to get credentials with specific role
    const cognitoIdentity = new AWS.CognitoIdentity({
      region: awsconfig.aws_cognito_region
    });

    // Construct the Cognito Identity Provider login key
    const cognitoLoginKey = `cognito-idp.${awsconfig.aws_cognito_region}.amazonaws.com/${awsconfig.aws_user_pools_id}`;

    // Get identity ID
    const identityParams = {
      IdentityPoolId: awsconfig.aws_cognito_identity_pool_id,
      Logins: {
        [cognitoLoginKey]: token
      }
    };

    const identityResponse = await cognitoIdentity.getId(identityParams).promise();

    // Get credentials for this identity with customRoleArn.
    // Admin users use the adminGroupRole so they have the rights to access all
    // customer buckets. Non-admin users use their own customer-specific role.
    const availableRoles = user.signInUserSession.idToken.payload['cognito:roles'] || [];
    const roleKey = isAdmin ? 'admin' : customerID;
    const roleArn = availableRoles.find(role =>
      role.includes(`${roleKey}GroupRole`)
    );

    if (!roleArn) {
      console.error(`❌ No role ARN found for: ${roleKey}`);
      throw new Error(`No role ARN found for: ${roleKey}`);
    }

    const credentialsParams = {
      IdentityId: identityResponse.IdentityId,
      Logins: {
        [cognitoLoginKey]: token
      },
      CustomRoleArn: roleArn, // Specify which role to use
    };

    const credentialsResponse = await cognitoIdentity.getCredentialsForIdentity(credentialsParams).promise();

    return {
      accessKeyId: credentialsResponse.Credentials.AccessKeyId,
      secretAccessKey: credentialsResponse.Credentials.SecretKey,
      sessionToken: credentialsResponse.Credentials.SessionToken,
      expiration: credentialsResponse.Credentials.Expiration,
    };
  } catch (error) {
    console.error('❌ Enhanced auth error:', error);
    return null;
  }
}

/**
 * Configure Storage with customer-specific credentials.
 * Stores credentials for use by getS3Object / putS3Object.
 * The bucket is always the one belonging to `customerID`, regardless of whether
 * the caller is an admin.
 */
export async function configureStorageForCustomer(customerID, bucketPrefix, environment, region) {
  const { Storage } = await import('aws-amplify');

  // Get credentials (admin role for admins, customer role for regular users)
  const credentials = await getEnhancedAuthCredentials(customerID);

  if (!credentials) {
    throw new Error(`Failed to get credentials for customer: ${customerID}`);
  }

  // Store credentials and config for use in getS3Object / putS3Object.
  // The bucket is always the customer's bucket, never the admin bucket.
  currentCustomerCredentials = credentials;
  currentCustomerConfig = {
    bucket: `${bucketPrefix}-${environment}-${customerID}`,
    region: region,
    customerID: customerID,
  };

  // Still configure Storage for any other operations that might use it
  Storage.configure({
    AWSS3: {
      bucket: `${bucketPrefix}-${environment}-${customerID}`,
      region: region,
    },
  });

  return credentials;
}

/**
 * Get a pre-signed URL for an S3 object using customer-specific credentials.
 * This bypasses Amplify Storage and uses the AWS SDK directly.
 *
 * @param {string} key - S3 object key (e.g., "document/xxx/xxx.pdf")
 * @param {Object} options - Options (download, expires, etc.)
 * @returns {Promise<string>} Pre-signed URL
 */
export async function getS3Object(key, options = {}) {
  if (!currentCustomerCredentials || !currentCustomerConfig) {
    throw new Error('No customer credentials configured. Call configureStorageForCustomer first.');
  }

  const { bucket, region } = currentCustomerConfig;

  const s3 = new AWS.S3({
    region: region,
    credentials: {
      accessKeyId: currentCustomerCredentials.accessKeyId,
      secretAccessKey: currentCustomerCredentials.secretAccessKey,
      sessionToken: currentCustomerCredentials.sessionToken,
    },
  });

  const params = {
    Bucket: bucket,
    Key: key,
    Expires: options.expires || 900, // 15 minutes default
  };

  // If download option is specified, set response headers
  if (options.download !== false) {
    params.ResponseContentDisposition = options.download === true
      ? 'attachment'
      : `attachment; filename="${key.split('/').pop()}"`;
  }

  const url = await s3.getSignedUrlPromise('getObject', params);

  return url;
}

/**
 * Upload a file to S3 using customer-specific credentials.
 * This bypasses Amplify Storage and uses the AWS SDK directly.
 *
 * @param {string} key - S3 object key (e.g., "document/xxx/file.pdf")
 * @param {File|Blob} file - File or Blob to upload
 * @param {Object} options - Options (contentType, progressCallback, etc.)
 * @returns {Promise<Object>} S3 upload result
 */
export async function putS3Object(key, file, options = {}) {
  if (!currentCustomerCredentials || !currentCustomerConfig) {
    throw new Error('No customer credentials configured. Call configureStorageForCustomer first.');
  }

  const { bucket, region } = currentCustomerConfig;

  const s3 = new AWS.S3({
    region: region,
    credentials: {
      accessKeyId: currentCustomerCredentials.accessKeyId,
      secretAccessKey: currentCustomerCredentials.secretAccessKey,
      sessionToken: currentCustomerCredentials.sessionToken,
    },
  });

  const params = {
    Bucket: bucket,
    Key: key,
    Body: file,
    ContentType: options.contentType || file.type || 'application/octet-stream',
  };

  // Use upload() which supports progress tracking
  const upload = s3.upload(params);

  // Attach progress callback if provided
  if (options.progressCallback) {
    upload.on('httpUploadProgress', (progress) => {
      options.progressCallback(progress);
    });
  }

  const result = await upload.promise();

  return result;
}
